import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineConfirmationNumber } from 'react-icons/md';
import { useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { getCurrentLocale } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import StackedBarChart from 'src/components/followUp/common/BaseWidgets/StackedBarChart';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useExportOptions } from './hooks';

const TotalTicketsWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('followUp');
  const { colors } = useTheme();
  const ariaId = useId();
  const stackLabels = [
    {
      name: t('npsGroup.promoter'),
      color: colors.tertiary[700],
    },
    {
      name: t('npsGroup.passive'),
      color: colors.neutral[500],
    },
    {
      name: t('npsGroup.detractor'),
      color: colors.error[600],
    },
  ];

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.TOTAL_TICKETS,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, queryFilters, comparisonData } =
    useWidgetQuery<StackedNumbersList>(endpoint);
  const locale = getCurrentLocale();

  const exportOptions = useExportOptions({
    data: data ?? null,
    comparisonData: comparisonData ?? null,
    stackLabels,
    widgetName: config.name,
    queryFilters: queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      error={errorMsg}
      loading={isFetching}
      exportOptions={exportOptions}
      header={
        <WidgetHeaderWithPill
          title={config.name}
          ariaId={ariaId}
          pillProps={{
            label: (data?.totalObjects ?? 0).toLocaleString(locale),
            icon: <MdOutlineConfirmationNumber />,
            color: 'primary',
          }}
        />
      }
      footer={
        data?.totalObjects === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : (
          <WidgetFooterLegend labels={stackLabels} />
        )
      }
    >
      {data && (
        <StackedBarChart
          bars={data.results.map((bar) => ({
            label: t(`status.${bar.label}`),
            stacks: bar.stacks.map((stack) => ({
              value: stack.value,
              stackLabel: t(`npsGroup.${stack.stackLabel}`),
            })),
          }))}
          stackLabels={stackLabels}
          showTotals
          scrollable
        />
      )}
    </WidgetFrame>
  );
};

export default TotalTicketsWidget;
